import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCar,
  FaMotorcycle,
  FaCarSide,
  FaEllipsisH,
  FaSearch,
  FaTruck
} from "react-icons/fa";
import { GiCargoShip } from "react-icons/gi";
import './HomeFilter.css';
import { useGetmakesModelsDataQuery } from "../../redux/createApi";

const CustomDropdown = ({ value, onChange, options, placeholder, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const selectedOption = options.find(opt => String(opt.value) === String(value)) || { label: placeholder, value: "" };

  return (
    <div className={`custom-dropdown-container ${disabled ? 'disabled' : ''}`} ref={dropdownRef}>
      <div
        className={`filter-select custom-dropdown-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className="text-truncate">{selectedOption.label}</span>
      </div>
      {isOpen && !disabled && (
        <div className="custom-dropdown-menu">
          {options.map((opt, index) => (
            <div
              key={index}
              className={`custom-dropdown-item ${String(value) === String(opt.value) ? 'selected' : ''}`}
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const HomeFilter = () => {
  const year = Array.from({ length: 2026 - 1990 + 1 }, (_, i) => 2026 - i);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Automobile");
  const [isArchived, setIsArchived] = useState(false);
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [fromYear, setFromYear] = useState("");
  const [toYear, setToYear] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isCopart, setIsCopart] = useState(true);
  const [isIAAI, setIsIAAI] = useState(true);

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    let filterData = {};
    if (searchQuery.trim() !== "") {
      filterData = {
        "search-type": "vin",
        "lot_id": searchQuery,
      };
    } else {
      filterData = {
        "search-type": "filters",
        "type": activeTab,
        "make": make,
        "model": model,
        "year-from": fromYear,
        "year-to": toYear,
        "auction-type": isCopart && !isIAAI
          ? 1
          : !isCopart && isIAAI
            ? 2
            : undefined,
      };
    }
    navigate("/quicksearch", { state: filterData });
  };

  const { data: filterdata } = useGetmakesModelsDataQuery({
    vehicle_type: activeTab
  });

  const isFilterDisabled = searchQuery.trim() !== "";

  const [showMore, setShowMore] = useState(false);

  const moreOptions = [
    "Watercraft",
    "Jet Sky",
    "Emergency Equipment",
    "Industrial Equipment",
    "Bus",
    "Other"
  ];

  const dynamicMakes = ["All makes", ...new Set((filterdata || []).map(item => item.make).filter(Boolean))];

  const dynamicModels =
    (make && make !== "All" && make !== "All makes")
      ? [...new Set(
        (filterdata || [])
          .filter(item => item.make === make)
          .flatMap(item => item.models || [])
      )]
      : [];

  return (
    <div className="container-fluid px-lg-5">
      <div className="home-filter-card">
        <div className={`filter-tabs-row ${isFilterDisabled ? "opacity-50 pointer-events-none" : ""}`}>
          <div className="filter-tabs">
            {[
              { id: "Automobile", icon: <FaCar /> },
              { id: "Motorcycle", icon: <FaMotorcycle /> },
              { id: "ATV", icon: <FaCarSide /> },
              { id: "Truck", icon: <FaTruck /> },
              { id: "Boat", icon: <GiCargoShip /> },
            ].map((tab) => (
              <div
                key={tab.id}
                className={`filter-tab ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => !isFilterDisabled && setActiveTab(tab.id)}
              >
                {tab.icon}
                <span>{tab.id}</span>
              </div>
            ))}

            <div
              className={`filter-tab more-tab-wrapper ${moreOptions.includes(activeTab) || activeTab === "More" ? "active" : ""}`}
              onClick={() => !isFilterDisabled && setShowMore(!showMore)}
            >
              <FaEllipsisH />
              <span>{moreOptions.includes(activeTab) ? activeTab : "More"}</span>

              {showMore && (
                <div onMouseLeave={() => setShowMore(false)} className="more-dropdown">
                  {moreOptions.map(option => (
                    <div
                      key={option}
                      className={`more-option ${activeTab === option ? "selected" : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveTab(option);
                        setShowMore(false);
                      }}
                    >
                      {option}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="archived-toggle-wrapper">
            <label className="switch-box">
              <input
                type="checkbox"
                checked={isArchived}
                onChange={(e) => !isFilterDisabled && setIsArchived(e.target.checked)}
                disabled={isFilterDisabled}
              />
              <span className="switch-slider grey-bg"></span>
            </label>
            <span>Archived</span>
          </div>
        </div>

        <div className="filter-content-grid">

          <div className={`filter-left-col ${isFilterDisabled ? "opacity-50 pointer-events-none" : ""}`}>
            <CustomDropdown
              value={make}
              onChange={setMake}
              disabled={isFilterDisabled}
              options={dynamicMakes.map(item => ({ label: item, value: item === "All makes" ? "All" : item }))}
              placeholder="All makes"
            />
            <CustomDropdown
              value={fromYear}
              onChange={setFromYear}
              disabled={isFilterDisabled}
              options={[
                { label: "Years From", value: "" },
                ...year.map(y => ({ label: y.toString(), value: y.toString() }))
              ]}
              placeholder="Years From"
            />
          </div>

          <div className={`filter-left-col ${isFilterDisabled ? "opacity-50 pointer-events-none" : ""}`}>
            <CustomDropdown
              value={model}
              onChange={setModel}
              disabled={isFilterDisabled}
              options={[
                { label: "All models", value: "All" },
                ...dynamicModels.map(item => ({ label: item, value: item }))
              ]}
              placeholder="All models"
            />
            <CustomDropdown
              value={toYear}
              onChange={setToYear}
              disabled={isFilterDisabled}
              options={[
                { label: "Years To", value: "" },
                ...year.map(y => ({ label: y.toString(), value: y.toString() }))
              ]}
              placeholder="Years To"
            />
          </div>

          <div className="or-divider">or</div>

          <div className="filter-left-col">
            <div className="vin-lot-search-container">
              <input
                type="text"
                className="filter-input"
                placeholder="Search by vehicle title"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
              />
              <button className="search-icon-btn mt-0" onClick={handleSubmit}>
                <FaSearch />
              </button>
            </div>

            <div className="filter-footer-row">
              <div className={`auction-source-toggles ${isFilterDisabled ? "opacity-50 pointer-events-none" : ""}`}>
                <div className="source-toggle-item">
                  <label className="switch-box">
                    <input
                      type="checkbox"
                      checked={isCopart}
                      onChange={(e) => !isFilterDisabled && setIsCopart(e.target.checked)}
                      disabled={isFilterDisabled}
                    />
                    <span className="switch-slider blue-bg"></span>
                  </label>
                  <span>Copart</span>
                </div>
                <div className="source-toggle-item">
                  <label className="switch-box">
                    <input
                      type="checkbox"
                      checked={isIAAI}
                      onChange={(e) => !isFilterDisabled && setIsIAAI(e.target.checked)}
                      disabled={isFilterDisabled}
                    />
                    <span className="switch-slider red-bg"></span>
                  </label>
                  <span>IAAI</span>
                </div>
              </div>

              <button className="filter-submit-btn mt-0" onClick={handleSubmit}>
                {isArchived ? "Archived offers" : "Show Vehicles"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeFilter;